import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import QuickAdd from "@/components/QuickAdd";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import ProspectRecontactDialog from "@/components/ProspectRecontactDialog";
import RecallPreventiviDialog from "@/components/RecallPreventiviDialog";
import { VisiteDaProgrammareDialog } from "@/components/VisiteDaProgrammareDialog";
import { buildDaProgrammare } from "@/lib/daProgrammare";
import { Loader2, CalendarClock, ClipboardCheck, Clock, FileClock, Skull, Users, AlertTriangle, FileSignature } from "lucide-react";
import { toast } from "sonner";
import PresidioDialog from "@/components/PresidioDialog";
import MergeDoppioneDialog from "@/components/MergeDoppioneDialog";
import AutoDedupDialog from "@/components/AutoDedupDialog";
import { Copy } from "lucide-react";

const TYPE_LABEL = { affiancamento_agenzia: "Affiancamento", visita_partner: "Visita", telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email/contatto", review_partner: "Review" };
const KIND = { cliente: { label: "Cliente", cls: "border-emerald-300 bg-emerald-100 text-emerald-800" }, partner: { label: "Partner", cls: "border-sky-300 bg-sky-100 text-sky-800" }, prospect: { label: "Prospect", cls: "border-amber-300 bg-amber-100 text-amber-800" } };
const PRESIDIO = {
  in_linea: { label: "In presidio", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-500" },
  in_scadenza: { label: "In scadenza", cls: "border-amber-500/40 bg-amber-500/15 text-amber-600" },
  scaduto: { label: "Scaduto", cls: "border-red-500/40 bg-red-500/15 text-red-500" },
};
const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function Anagrafiche() {
  const { user } = useAuth();
  const insertActor = user?.role === "agency" ? "agenzia" : "responsabile_commerciale";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [sconfinati, setSconfinati] = useState(new Set());
  const [prospects, setProspects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [items, setItems] = useState([]);
  const [reactItems, setReactItems] = useState([]);
  const [recallItems, setRecallItems] = useState([]);
  const [showDaProg, setShowDaProg] = useState(false);
  const [showProspect, setShowProspect] = useState(false);
  const [showRecall, setShowRecall] = useState(false);
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [visitaDefault, setVisitaDefault] = useState({ partnerId: null, prospectId: null });
  const [qaOpen, setQaOpen] = useState(false);
  const [qaDefault, setQaDefault] = useState({ partnerId: null, partnerName: "", prospectId: null, prospectName: "" });

  // filtri
  const [view, setView] = useState("attivo");
  const [presidioDlg, setPresidioDlg] = useState({ open: false, kind: "partners", target: null, mode: "move" });
  const canMove = ["admin", "commercial_manager", "agency"].includes(user?.role);
  const canAutoDedup = user?.role === "admin";
  const [autoDedupOpen, setAutoDedupOpen] = useState(false);
  const canImportRef = ["admin", "head_of_partner"].includes(user?.role);
  const [refImp, setRefImp] = useState(null);
  const pollRef = useRef(null);
  const pollRefStatus = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/import/fier-referenti-status");
        setRefImp(data);
        if (data.finished || !data.running) { clearInterval(pollRef.current); if (data.error) toast.error(data.message); else toast.success(data.message || "Import referenti completato"); }
      } catch { /* ignore */ }
    }, 3000);
  };
  const startRefImport = async () => {
    if (!window.confirm("Avviare l'import una tantum dei referenti dal gestionale Fier? Può richiedere diversi minuti.")) return;
    try {
      await api.post("/import/fier-referenti-start");
      setRefImp({ running: true, total: 0, processed: 0, message: "Avvio…" });
      pollRefStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore avvio import"); }
  };
  const [contrImp, setContrImp] = useState(null);
  const pollContr = useRef(null);
  const pollContrStatus = () => {
    clearInterval(pollContr.current);
    pollContr.current = setInterval(async () => {
      try {
        const { data } = await api.get("/import/fier-contratti-status");
        setContrImp(data);
        if (data.finished || !data.running) { clearInterval(pollContr.current); if (data.error) toast.error(data.message); else toast.success(data.message || `Import contratti completato: ${data.imported || 0} importati`); }
      } catch { /* ignore */ }
    }, 3000);
  };
  const startContrImport = async () => {
    if (!window.confirm("Avviare l'import una tantum dei contratti partnership dal gestionale Fier? Scarica i PDF e calcola la scadenza a +1 anno. Può richiedere diversi minuti.")) return;
    try {
      await api.post("/import/fier-contratti-start");
      setContrImp({ running: true, total: 0, processed: 0, imported: 0, message: "Avvio…" });
      pollContrStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore avvio import"); }
  };
  useEffect(() => {
    if (canImportRef) {
      api.get("/import/fier-referenti-status").then(({ data }) => { if (data.running) { setRefImp(data); pollRefStatus(); } }).catch(() => {});
      api.get("/import/fier-contratti-status").then(({ data }) => { if (data.running) { setContrImp(data); pollContrStatus(); } }).catch(() => {});
    }
    return () => { clearInterval(pollRef.current); clearInterval(pollContr.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [fltKind, setFltKind] = useState("all");
  const [fltRegion, setFltRegion] = useState("all");
  const [fltProvince, setFltProvince] = useState("all");
  const [q, setQ] = useState("");
  const [onlyDup, setOnlyDup] = useState(false);
  const [mergeSrc, setMergeSrc] = useState(null);

  const load = (v = view) => { setLoading(true); return api.get("/anagrafiche", { params: { presidio: v } }).then((r) => { setRows(r.data || []); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(view); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view]);
  useEffect(() => {
    api.get("/activities?limit=3000").then((r) => setItems(r.data.items || r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data || [])).catch(() => {});
    if (["admin", "back_office"].includes(user?.role)) api.get("/crm-quotes/affidamenti-sconfinati").then((r) => setSconfinati(new Set(r.data.ids || []))).catch(() => {});
    api.get("/prospects").then((r) => setProspects(r.data || [])).catch(() => {});
    api.get("/agenda/riattivazione").then((r) => setReactItems(r.data.items || [])).catch(() => {});
    api.get("/quotes/recall").then((r) => setRecallItems(r.data.items || [])).catch(() => {});
    if (user?.role === "admin") api.get("/users?role=commercial_manager").then((r) => setManagers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => { load(); api.get("/activities?limit=3000").then((r) => setItems(r.data.items || r.data)).catch(() => {}); };
  const daProgrammare = useMemo(() => buildDaProgrammare(reactItems, items), [items, reactItems]);
  const prospectDue = useMemo(() => {
    const t = new Date().toISOString().slice(0, 10);
    const HIDDEN = ["vinto", "ordine", "perso"];
    return (prospects || []).filter((p) => p.next_action_date && String(p.next_action_date).slice(0, 10) <= t && !HIDDEN.includes(p.stage)).sort((a, b) => String(a.next_action_date).localeCompare(String(b.next_action_date)));
  }, [prospects]);

  const openAttivitaFor = (rec) => { setShowDaProg(false); setShowProspect(false); setQaDefault({ partnerId: rec.kind === "partner" ? rec.id : null, partnerName: rec.kind === "partner" ? rec.name : "", prospectId: rec.kind === "prospect" ? rec.id : null, prospectName: rec.kind === "prospect" ? rec.name : "" }); setQaOpen(true); };
  const openVisitaFor = (rec) => { setShowDaProg(false); setShowProspect(false); setVisitaDefault({ partnerId: rec.kind === "partner" ? rec.id : null, prospectId: rec.kind === "prospect" ? rec.id : null }); setVisitaOpen(true); };

  const regions = useMemo(() => {
    const m = new Map(); // chiave normalizzata -> etichetta migliore
    for (const r of rows) {
      const v = (r.region || "").trim();
      if (!v) continue;
      const k = v.toLowerCase();
      const score = (v[0] === (v[0] || "").toUpperCase() ? 2 : 0) + (v !== v.toUpperCase() ? 1 : 0);
      const cur = m.get(k);
      if (!cur || score > cur.score) m.set(k, { label: v, score });
    }
    return [...m.entries()].map(([key, o]) => ({ key, label: o.label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const provinces = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (fltRegion !== "all" && (r.region || "").trim().toLowerCase() !== fltRegion) continue;
      const v = (r.province || "").trim();
      if (v) s.add(v.toUpperCase());
    }
    return [...s].sort();
  }, [rows, fltRegion]);

  const dupCount = useMemo(() => rows.filter((r) => r.dup_warning).length, [rows]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyDup && !r.dup_warning) return false;
      if (fltKind !== "all" && r.kind !== fltKind) return false;
      if (fltRegion !== "all" && (r.region || "").trim().toLowerCase() !== fltRegion) return false;
      if (fltProvince !== "all" && (r.province || "").trim().toUpperCase() !== fltProvince) return false;
      if (term && !((r.name || "").toLowerCase().includes(term) || (r.city || "").toLowerCase().includes(term) || (r.province || "").toLowerCase().includes(term) || (r.agente || "").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [rows, fltKind, fltRegion, fltProvince, q, onlyDup]);

  const hrefFor = (r) => (r.kind === "prospect" ? `/prospect/${r.id}` : `/partners/${r.id}`);

  return (
    <div className="space-y-6" data-testid="anagrafiche-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary" /><h1 className="font-heading text-2xl font-bold tracking-tight">Anagrafiche</h1></div>
          <p className="text-sm text-muted-foreground">Elenco completo delle anagrafiche di tua competenza.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {user?.role !== "technical" && (
          <>
          <div className="flex items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-crea">
            <QuickAdd onChanged={reload} show={["activity"]} />
            <Button size="sm" onClick={() => { setVisitaDefault({ partnerId: null, prospectId: null }); setVisitaOpen(true); }} data-testid="agenda-registra-visita-btn"><ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita</Button>
          </div>
          <div data-testid="agenda-group-prospect"><QuickAdd onChanged={reload} show={["prospect"]} variant="default" /></div>
          </>
          )}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-liste">
            <Button variant="outline" size="sm" onClick={() => setShowDaProg(true)} data-testid="agenda-da-programmare-btn"><CalendarClock className="mr-2 h-4 w-4" /> Visite da programmare{daProgrammare.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{daProgrammare.length}</Badge>}</Button>
            <Button variant="outline" size="sm" onClick={() => setShowProspect(true)} data-testid="agenda-prospect-recall-btn"><Clock className="mr-2 h-4 w-4" /> Prospect da ricontattare{prospectDue.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{prospectDue.length}</Badge>}</Button>
            <Button variant="outline" size="sm" onClick={() => setShowRecall(true)} data-testid="agenda-preventivi-recall-btn"><FileClock className="mr-2 h-4 w-4" /> Recall preventivi{recallItems.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{recallItems.length}</Badge>}</Button>
          </div>
          <Button variant={view === "inactive" ? "default" : "outline"} size="sm" onClick={() => { setOnlyDup(false); setView(view === "inactive" ? "attivo" : "inactive"); }} data-testid="anagrafiche-cimitero-toggle" title="Mostra solo Panchina / Cimitero"><Skull className="mr-2 h-4 w-4" /> Panchina / Cimitero</Button>
          {canAutoDedup && (
            <Button variant="outline" size="sm" onClick={() => setAutoDedupOpen(true)} data-testid="anagrafiche-auto-dedup-btn" title="Unisci automaticamente i doppioni con stesso nome nel record con P.IVA" className="border-sky-500/60 text-sky-600 hover:bg-sky-500/10"><Copy className="mr-2 h-4 w-4" /> Deduplica ora</Button>
          )}
          {view !== "inactive" && dupCount > 0 && (
            <Button variant={onlyDup ? "default" : "outline"} size="sm" onClick={() => setOnlyDup((v) => !v)} data-testid="anagrafiche-dup-toggle" title="Anagrafiche senza P.IVA con nome duplicato: possibili doppioni da verificare/unire a mano" className={onlyDup ? "bg-amber-500 text-white hover:bg-amber-500/90" : "border-amber-500/60 text-amber-600 hover:bg-amber-500/10"}>
              <AlertTriangle className="mr-2 h-4 w-4" /> {onlyDup ? "Mostra tutte" : "Da verificare"}<Badge className="ml-2 bg-amber-600 text-white">{dupCount}</Badge>
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-end gap-2" data-testid="anagrafiche-filters">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Tipo</Label>
            <Select value={fltKind} onValueChange={setFltKind}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="flt-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="partner">Partner</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Regione</Label>
            <Select value={fltRegion} onValueChange={(v) => { setFltRegion(v); setFltProvince("all"); }}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="flt-region"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le regioni</SelectItem>{regions.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Provincia</Label>
            <Select value={fltProvince} onValueChange={setFltProvince}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="flt-province"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1">
            <Label className="text-[11px] text-muted-foreground">Ricerca libera</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, città, provincia…" className="h-8 min-w-[200px] text-xs" data-testid="flt-text" />
          </div>
          <span className="text-[11px] text-muted-foreground" data-testid="anagrafiche-count">{filtered.length} anagrafiche</span>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Anagrafica</TableHead>
                <TableHead>Agente</TableHead>
                <TableHead>Presidio</TableHead>
                <TableHead>Ultima azione</TableHead>
                <TableHead>Prossima azione raccomandata</TableHead>
                {canMove && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={canMove ? 6 : 5} className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>}
              {!loading && filtered.slice(0, 500).map((r) => {
                const k = KIND[r.kind] || KIND.cliente;
                return (
                  <TableRow key={`${r.kind}-${r.id}`} data-testid={`anagrafica-row-${r.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${k.cls}`}>{k.label}</Badge>
                        <Link to={hrefFor(r)} className="font-semibold text-primary hover:underline" data-testid={`anagrafica-link-${r.id}`}>{r.name || "—"}</Link>
                        {sconfinati.has(r.id) && <Badge className="ml-2 bg-red-600 text-white text-[10px]" data-testid={`anag-sconfino-${r.id}`}>Oltre fido</Badge>}
                        {r.dup_warning && <Badge variant="outline" className="ml-1 border-amber-500/60 bg-amber-500/15 text-amber-600 text-[10px]" data-testid={`anag-dup-warning-${r.id}`} title="Anagrafica senza P.IVA e con nome duplicato: possibile doppione da verificare/unire a mano">⚠ Senza P.IVA · doppione?</Badge>}
                        {view === "inactive" && r.presidio_status && <Badge variant="outline" className={`ml-1 text-[10px] ${r.presidio_status === "cimitero" ? "border-red-500/40 text-red-400" : "border-amber-500/40 text-amber-400"}`} data-testid={`anag-db-${r.id}`}>{r.presidio_status === "cimitero" ? "Cimitero" : "Panchina"}</Badge>}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{[r.city, r.province, r.region].filter(Boolean).join(" · ") || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`anagrafica-agente-${r.id}`}>{r.agente || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{r.presidio_linea && PRESIDIO[r.presidio_linea] ? <Badge variant="outline" className={`text-[11px] ${PRESIDIO[r.presidio_linea].cls}`} data-testid={`anagrafica-presidio-${r.id}`}>{PRESIDIO[r.presidio_linea].label}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">{r.last_action ? <span>{itDate(r.last_action.date)}<span className="ml-1 text-muted-foreground">· {TYPE_LABEL[r.last_action.type] || r.last_action.type || ""}</span></span> : <span className="text-muted-foreground">Nessuna</span>}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[11px]">{r.next_reco || "—"}</Badge></TableCell>
                    {canMove && (
                      <TableCell className="text-right">
                        {view === "inactive"
                          ? <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-primary" onClick={() => setPresidioDlg({ open: true, kind: r.kind === "prospect" ? "prospects" : "partners", target: { id: r.id, name: r.name }, mode: "restore" })} data-testid={`anag-restore-${r.id}`}>Riporta attivo</Button>
                          : (
                            <div className="flex items-center justify-end gap-1">
                              {r.dup_warning && <Button variant="outline" size="sm" className="h-7 px-2 text-[10px] border-amber-500/60 text-amber-600 hover:bg-amber-500/10" onClick={() => setMergeSrc({ kind: r.kind, id: r.id, name: r.name })} data-testid={`anag-merge-${r.id}`}><AlertTriangle className="mr-1 h-3 w-3" /> Unisci con…</Button>}
                              <Button variant="ghost" size="icon" title="Panchina / Cimitero" onClick={() => setPresidioDlg({ open: true, kind: r.kind === "prospect" ? "prospects" : "partners", target: { id: r.id, name: r.name }, mode: "move" })} data-testid={`anag-bench-${r.id}`}><Skull className="h-4 w-4 text-amber-500" /></Button>
                            </div>
                          )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={canMove ? 6 : 5} className="py-10 text-center text-muted-foreground">Nessuna anagrafica trovata.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <VisiteDaProgrammareDialog open={showDaProg} onOpenChange={setShowDaProg} items={daProgrammare} onChanged={reload} actor={insertActor} ownerId={user?.role === "agency" ? user.id : null} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      <ProspectRecontactDialog open={showProspect} onOpenChange={setShowProspect} items={prospectDue} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      <RecallPreventiviDialog open={showRecall} onOpenChange={setShowRecall} items={recallItems} />
      <VisitaB2BDialog open={visitaOpen} onOpenChange={setVisitaOpen} partners={partners} prospects={prospects} managers={managers} actor={insertActor} defaultPartnerId={visitaDefault.partnerId} defaultProspectId={visitaDefault.prospectId} onSaved={reload} />
      <QuickAdd hideTriggers activityOpen={qaOpen} onActivityOpenChange={setQaOpen} partnerId={qaDefault.partnerId} partnerName={qaDefault.partnerName} prospectId={qaDefault.prospectId} prospectName={qaDefault.prospectName} onChanged={reload} show={["activity"]} />
      <PresidioDialog open={presidioDlg.open} onOpenChange={(o) => setPresidioDlg((s) => ({ ...s, open: o }))} kind={presidioDlg.kind} target={presidioDlg.target} mode={presidioDlg.mode} onDone={reload} />
      <MergeDoppioneDialog source={mergeSrc} onOpenChange={(o) => { if (!o) setMergeSrc(null); }} onDone={reload} />
      <AutoDedupDialog open={autoDedupOpen} onOpenChange={setAutoDedupOpen} onDone={reload} />
    </div>
  );
}
